import type { CSSProperties, ReactNode } from 'react'
import { Icon } from './icons'

type ButtonKind = 'primary' | 'dark' | 'secondary' | 'ghost' | 'coral' | 'onDark' | 'outline' | 'danger'
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'

const sizes: Record<ButtonSize, CSSProperties> = {
  xs: { padding: '5px 10px', fontSize: 12, gap: 5 },
  sm: { padding: '7px 12px', fontSize: 13, gap: 6 },
  md: { padding: '10px 16px', fontSize: 14, gap: 7 },
  lg: { padding: '13px 22px', fontSize: 15, gap: 8 },
}

const kinds: Record<ButtonKind, CSSProperties> = {
  primary:   { background: 'var(--brand-500)', color: 'white', boxShadow: '0 1px 0 rgba(255,255,255,.2) inset, 0 4px 14px rgba(37,87,232,0.25)' },
  dark:      { background: 'var(--brand-900)', color: 'white' },
  secondary: { background: 'white', color: 'var(--brand-900)', border: '1px solid var(--border-default)' },
  ghost:     { background: 'transparent', color: 'var(--brand-700)' },
  coral:     { background: 'var(--accent-500)', color: 'white' },
  onDark:    { background: 'white', color: 'var(--brand-700)' },
  outline:   { background: 'transparent', color: 'var(--brand-700)', border: '1px solid var(--brand-300)' },
  danger:    { background: 'white', color: 'var(--danger)', border: '1px solid var(--danger-100)' },
}

interface ButtonProps {
  kind?: ButtonKind
  size?: ButtonSize
  icon?: ReactNode
  onClick?: () => void
  style?: CSSProperties
  children?: ReactNode
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  ariaLabel?: string
  ariaPressed?: boolean
}

export function Button({ kind = 'primary', size = 'md', icon, onClick, style, children, disabled, type = 'button', ariaLabel, ariaPressed }: ButtonProps) {
  const iconSize = size === 'lg' ? 18 : size === 'xs' ? 13 : 15
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      style={{
        fontFamily: 'inherit',
        fontWeight: 600,
        borderRadius: 10,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: '1px solid transparent',
        transition: 'all 200ms cubic-bezier(0.22,1,0.36,1)',
        display: 'inline-flex',
        alignItems: 'center',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        opacity: disabled ? 0.5 : 1,
        ...sizes[size],
        ...kinds[kind],
        ...style,
      }}
    >
      {icon && <Icon size={iconSize}>{icon}</Icon>}
      {children}
    </button>
  )
}
